"use client";

import { Button, Checkbox, Label, Loader, Modal, toast } from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import type {
  ShopBranchAdmin,
  ShopStaff,
} from "@/modules/merchant/presentation/api/client";
import { shopStaffApi } from "@/modules/merchant/presentation/api/client";
import { shopStaffKeys } from "../api/queries";

async function fetchAllShopStaff(shopId: string): Promise<ShopStaff[]> {
  const out: ShopStaff[] = [];
  let offset = 0;
  const limit = 100;
  while (true) {
    const r = await shopStaffApi.list(shopId, {
      limit,
      offset,
      expandAssignments: true,
    });
    out.push(...r.data);
    if (r.data.length < limit || out.length >= r.meta.total) break;
    offset += limit;
  }
  return out;
}

type StaffByUser = {
  userId: string;
  userName: string | null;
  userEmail: string;
  rows: ShopStaff[];
  /** ມີແຖວ branch_id null = ເຂົ້າໄດ້ທຸກສາຂາ */
  shopWide: boolean;
};

function groupStaffByUser(rows: ShopStaff[]): StaffByUser[] {
  const m = new Map<string, ShopStaff[]>();
  for (const r of rows) {
    const list = m.get(r.userId) ?? [];
    list.push(r);
    m.set(r.userId, list);
  }
  return [...m.entries()]
    .map(([userId, groupRows]) => ({
      userId,
      userName: groupRows[0]?.userName ?? null,
      userEmail: groupRows[0]?.userEmail ?? "",
      rows: groupRows,
      shopWide: groupRows.some((x) => x.branchId == null),
    }))
    .sort((a, b) =>
      a.userEmail.localeCompare(b.userEmail, undefined, {
        sensitivity: "base",
      }),
    );
}

function branchAssignmentLabel(
  g: StaffByUser,
  branches: ShopBranchAdmin[],
): string {
  if (g.shopWide) return "ທຸກສາຂາ";
  const parts = g.rows
    .filter((r) => r.branchId != null)
    .map(
      (r) =>
        r.branchName?.trim() ||
        branches.find((b) => b.id === r.branchId)?.name ||
        `${String(r.branchId).slice(0, 8)}…`,
    );
  return [...new Set(parts)].join(", ") || "—";
}

type ShopAssignStaffToBranchModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopId: string;
  branches: ShopBranchAdmin[];
  /** ມາຈາກແຖວສາຂາ — ລັອກສາຂາ; null = ເລືອກສາຂາໃນ modal (ປຸ່ມເທິງ) */
  initialBranch: ShopBranchAdmin | null;
  canUpdateStaff: boolean;
  onSuccess?: () => void;
};

export function ShopAssignStaffToBranchModal({
  open,
  onOpenChange,
  shopId,
  branches,
  initialBranch,
  canUpdateStaff,
  onSuccess,
}: ShopAssignStaffToBranchModalProps) {
  const qc = useQueryClient();
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [staffRows, setStaffRows] = useState<ShopStaff[]>([]);
  const [checkedUserIds, setCheckedUserIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const branchLocked = initialBranch != null;
  const targetBranchId = branchLocked ? initialBranch.id : selectedBranchId;

  const branchLabel = useMemo(() => {
    const id = targetBranchId;
    if (!id) return "";
    return branches.find((b) => b.id === id)?.name ?? id.slice(0, 8);
  }, [branches, targetBranchId]);

  const staffByUser = useMemo(() => groupStaffByUser(staffRows), [staffRows]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync branch picker when modal opens
  useEffect(() => {
    if (!open) return;
    if (initialBranch) setSelectedBranchId(initialBranch.id);
    else setSelectedBranchId("");
  }, [open, initialBranch?.id]);

  useEffect(() => {
    if (!open || !shopId || !targetBranchId) {
      if (!open) {
        setStaffRows([]);
        setCheckedUserIds(new Set());
      }
      return;
    }

    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const rows = await fetchAllShopStaff(shopId);
        if (cancelled) return;
        setStaffRows(rows);
        const groups = groupStaffByUser(rows);
        const nextChecked = new Set<string>();
        for (const g of groups) {
          if (g.shopWide || g.rows.some((r) => r.branchId === targetBranchId)) {
            nextChecked.add(g.userId);
          }
        }
        setCheckedUserIds(nextChecked);
      } catch {
        if (!cancelled) {
          setStaffRows([]);
          setCheckedUserIds(new Set());
          toast.error("ບໍ່ສາມາດໂຫຼດລາຍການພະນັກງານ");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, shopId, targetBranchId]);

  const toggle = (userId: string, shopWide: boolean, on: boolean) => {
    if (shopWide) return;
    setCheckedUserIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(userId);
      else next.delete(userId);
      return next;
    });
  };

  const selectAll = () => {
    setCheckedUserIds(new Set(staffByUser.map((g) => g.userId)));
  };

  const selectNone = () => {
    const next = new Set<string>();
    for (const g of staffByUser) {
      if (g.shopWide) next.add(g.userId);
    }
    setCheckedUserIds(next);
  };

  const onSave = async () => {
    if (!targetBranchId || !canUpdateStaff) return;
    setSaving(true);
    try {
      const creates: { userId: string; shopRoleId: string | null }[] = [];
      const deleteStaffIds: string[] = [];

      for (const g of staffByUser) {
        if (g.shopWide) continue;
        const want = checkedUserIds.has(g.userId);
        const rowOnTarget = g.rows.find((r) => r.branchId === targetBranchId);
        if (want && !rowOnTarget) {
          const template = g.rows[0];
          creates.push({
            userId: g.userId,
            shopRoleId: template?.shopRoleId ?? null,
          });
        }
        if (!want && rowOnTarget) {
          deleteStaffIds.push(rowOnTarget.id);
        }
      }

      if (creates.length === 0 && deleteStaffIds.length === 0) {
        toast.success("ບໍ່ມີການປ່ຽນແປງ");
        onOpenChange(false);
        return;
      }

      for (const c of creates) {
        await shopStaffApi.create(shopId, {
          userId: c.userId,
          shopRoleId: c.shopRoleId,
          branchId: targetBranchId,
        });
      }
      for (const id of deleteStaffIds) {
        await shopStaffApi.delete(shopId, id);
      }

      toast.success("ມອບໝາຍສາຂາສຳເລັດ");
      qc.invalidateQueries({ queryKey: shopStaffKeys.all(shopId) });
      onSuccess?.();
      onOpenChange(false);
    } catch {
      toast.error("ບັນທຶກລົ້ມເຫຼວ");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="ມອບໝາຍພະນັກງານເຂົ້າສາຂາ"
      description={
        targetBranchId
          ? `ຜູ້ໃຊ້ຄົນດຽວສາມາດມີຫຼາຍສາຂາ — ຕິກ = ມີສິດທີ່ສາຂາ “${branchLabel}”, ຖອກ = ລົບສິດສາຂານີ້ເທົ່ານັ້ນ (ບໍ່ລົບບັນຊີ)`
          : "ເລືອກສາຂາກ່ອນ ຈາກນັ້ນຕິກພະນັກງານທີ່ຕ້ອງການ"
      }
      size="lg"
    >
      <div className="flex max-h-[min(420px,60vh)] flex-col gap-4">
        {!branchLocked ? (
          <div className="space-y-2">
            <Label htmlFor="assign-branch-select">ສາຂາ</Label>
            <select
              id="assign-branch-select"
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={selectedBranchId}
              onChange={(e) => setSelectedBranchId(e.target.value)}
            >
              <option value="">— ເລືອກສາຂາ —</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id} disabled={!b.isActive}>
                  {b.name}
                  {!b.isActive ? " (ປິດ)" : ""}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {loading ? (
          <div className="flex min-h-[160px] items-center justify-center py-6">
            <Loader />
          </div>
        ) : !targetBranchId ? (
          <p className="text-muted-foreground text-sm">
            ເລືອກສາຂາກ່ອນເພື່ອໂຫຼດລາຍການ.
          </p>
        ) : staffByUser.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            ຍັງບໍ່ມີພະນັກງານໃນຮ້ານ — ເພີ່ມພະນັກງານທີ່ໜ້າ ພະນັກງານ ກ່ອນ.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 border-b pb-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={selectAll}
                disabled={!canUpdateStaff}
              >
                ເລືອກທັງໝົດ
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={selectNone}
                disabled={!canUpdateStaff}
              >
                ຖອກທັງໝົດ
              </Button>
            </div>
            <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-1">
              {staffByUser.map((g) => (
                <li
                  key={g.userId}
                  className="flex items-start gap-3 rounded-md border border-transparent px-2 py-2 hover:bg-muted/40"
                >
                  <Checkbox
                    id={`assign-staff-${g.userId}`}
                    checked={checkedUserIds.has(g.userId)}
                    onCheckedChange={(c) =>
                      toggle(g.userId, g.shopWide, c === true)
                    }
                    disabled={!canUpdateStaff || g.shopWide}
                  />
                  <label
                    htmlFor={`assign-staff-${g.userId}`}
                    className="min-w-0 flex-1 cursor-pointer"
                  >
                    <span className="block font-medium text-foreground text-sm">
                      {g.userName?.trim() || g.userEmail}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {g.userEmail}
                      {" · "}
                      {g.shopWide
                        ? "ສິດທຸກສາຂາ (ບໍ່ຕ້ອງມອບໝາຍທີ່ນີ້)"
                        : `ສາຂາປັດຈຸບັນ: ${branchAssignmentLabel(g, branches)}`}
                    </span>
                    {!g.shopWide && g.rows.some((r) => !r.isActive) ? (
                      <span className="text-muted-foreground text-xs">
                        {" "}
                        · ມີແຖວບໍ່ໄດ້ໃຊ້ງານ
                      </span>
                    ) : null}
                  </label>
                </li>
              ))}
            </ul>
          </>
        )}

        <div className="flex justify-end gap-2 border-t pt-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="button"
            onClick={() => void onSave()}
            disabled={
              !targetBranchId ||
              !canUpdateStaff ||
              loading ||
              saving ||
              staffByUser.length === 0
            }
          >
            {saving ? <Loader /> : null}
            <span className="ml-2">ບັນທຶກ</span>
          </Button>
        </div>
      </div>
    </Modal>
  );
}
